/**
 * NetworkDiscoveredList — «Dispositivos descubiertos».
 *
 * Una sola lista con dos conjuntos, que es como se revisa el descubrimiento:
 *   · **Dispositivos**: switches, routers, firewalls y APs que el colector ha
 *     dado de alta (o actualizado) en las tablas normales del CMDB.
 *   · **Equipos finales**: MAC/IP vistos en ARP/FDB que NO son un CI todavía.
 *     Se pueden ignorar para que dejen de molestar.
 *
 * Lista con el estándar: `ListPage`, filtro de conjunto + búsqueda + export.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SignalIcon, NoSymbolIcon } from '@heroicons/react/24/outline';
import ListPage from './common/ListPage';
import { hace } from '../utils/relativeTime';
import api from '../services/api';

/*
 * Solo el switch tiene listado propio que entienda `?destacar=<id>` con el id
 * del `RackElement`. Router, firewall y gateway usan otras tablas con ids
 * distintos: enlazarlos aquí abriría una ficha equivocada. Para esos tipos
 * queda el botón de eliminar, que es la vía para limpiar el descubierto.
 */
const ELEMENT_PATH = {
  switch: '/infrastructure/switches',
};

const SOURCE_LABEL = {
  snmp: 'SNMP',
  ssh: 'SSH',
  unifi: 'UniFi',
  api: 'API',
};

const ELEMENT_LABEL = {
  switch: 'Switch',
  router: 'Router',
  firewall: 'Firewall',
  gateway: 'Gateway',
  hub: 'Hub',
};

export default function NetworkDiscoveredList({ companyId }) {
  const navigate = useNavigate();

  const [devices, setDevices] = useState([]);
  const [endpoints, setEndpoints] = useState([]);
  const [ssids, setSsids] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [search, setSearch] = useState('');
  const [dataset, setDataset] = useState('devices');
  const [pending, setPending] = useState(null);

  const avisar = (texto) => {
    setNotice(texto);
    setTimeout(() => setNotice(''), 3000);
  };

  const cargar = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.getNetworkDiscovered(companyId);
      setDevices(Array.isArray(res?.devices) ? res.devices : []);
      setEndpoints(Array.isArray(res?.endpoints) ? res.endpoints : []);
      setSsids(Array.isArray(res?.ssids) ? res.ssids : []);
    } catch (e) {
      setError(e.message || 'No se pudieron cargar los dispositivos descubiertos');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { cargar(); }, [cargar]);

  const ignorar = async (endpoint) => {
    setPending(endpoint.id);
    try {
      await api.updateDiscoveredEndpoint(companyId, endpoint.id, { ignored: true });
      avisar('Equipo marcado como ignorado');
      await cargar();
    } catch (e) {
      setError(e.message || 'No se pudo ignorar el equipo');
    } finally {
      setPending(null);
    }
  };

  /**
   * Borrar un equipo descubierto elimina su **ficha real** del CMDB: puertos,
   * cables y la IP de gestión incluidos. Es la vía para limpiar fichas
   * huérfanas: si el equipo sigue respondiendo, el próximo barrido lo vuelve a
   * dar de alta con los datos que reporte.
   */
  const borrarDispositivo = async (device) => {
    try {
      await api.deleteDiscoveredDevice(companyId, device.id);
      avisar('Equipo eliminado. El próximo barrido lo volverá a crear si sigue respondiendo.');
      await cargar();
    } catch (e) {
      setError(e.message || 'No se pudo borrar el equipo descubierto');
    }
  };

  const borrarEndpoint = async (endpoint) => {
    try {
      await api.deleteDiscoveredEndpoint(companyId, endpoint.id);
      avisar('Equipo final eliminado');
      await cargar();
    } catch (e) {
      setError(e.message || 'No se pudo borrar el equipo final');
    }
  };

  const borrarSsid = async (ssid) => {
    try {
      await api.deleteDiscoveredSsid(companyId, ssid.id);
      avisar('SSID eliminada. Volverá a aparecer si el controlador la sigue emitiendo.');
      await cargar();
    } catch (e) {
      setError(e.message || 'No se pudo borrar la SSID descubierta');
    }
  };

  const esDispositivos = dataset === 'devices';
  const esSsids = dataset === 'ssids';

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const source = esDispositivos ? devices : esSsids ? ssids : endpoints;
    if (!term) return source;
    return source.filter((row) => (
      [
        row.name, row.ip_address, row.mac_address, row.manufacturer, row.model, row.serial_number,
        row.hostname, row.discovery_source, row.vlan_id, row.security_mode_label,
        row.controller_name, row.access_point_name,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    ));
  }, [devices, endpoints, ssids, search, esDispositivos, esSsids]);

  const columns = useMemo(() => {
    const dash = <span className="list-hint">—</span>;
    if (esSsids) {
      return [
        {
          key: 'name',
          label: 'SSID',
          sortable: true,
          render: (row) => (
            <span style={{ fontWeight: 600 }}>{row.name || dash}</span>
          ),
        },
        {
          key: 'security_mode',
          label: 'Seguridad',
          sortable: true,
          sortValue: (row) => row.security_mode_label || row.security_mode || '',
          render: (row) => row.security_mode_label || row.security_mode || dash,
        },
        {
          key: 'vlan_id',
          label: 'VLAN',
          sortable: true,
          render: (row) => (row.vlan_id ? row.vlan_id : dash),
        },
        {
          key: 'band_filter',
          label: 'Bandas',
          sortable: true,
          sortValue: (row) => row.band_filter_label || row.band_filter || '',
          render: (row) => (
            <span>
              {row.band_filter_label || dash}
              {row.hidden ? <span className="list-hint"> · oculta</span> : null}
            </span>
          ),
        },
        {
          key: 'enabled',
          label: 'Estado',
          sortable: true,
          sortValue: (row) => (row.enabled ? 1 : 0),
          render: (row) => (
            <span className={`list-badge ${row.enabled ? 'list-badge--on' : 'list-badge--off'}`}>
              {row.enabled ? 'Activa' : 'Desactivada'}
            </span>
          ),
        },
        {
          key: 'managed',
          label: 'Gestionada por el controlador',
          sortable: true,
          sortValue: (row) => (row.managed ? 1 : 0),
          render: (row) => (row.managed ? (
            <span>
              <span className="list-badge list-badge--on">Sí</span>
              {row.controller_name ? <span className="list-hint"> · {row.controller_name}</span> : null}
            </span>
          ) : (
            <span className="list-badge list-badge--off" title="La emite un AP pero no está en la configuración del controlador">
              No (AP local)
            </span>
          )),
        },
        {
          key: 'last_discovery_at',
          label: 'Última vez',
          sortable: true,
          sortValue: (row) => row.last_discovery_at || '',
          render: (row) => (row.last_discovery_at
            ? <span className="list-hint">{hace(row.last_discovery_at)}</span>
            : dash),
        },
      ];
    }
    if (!esDispositivos) {
      return [
        {
          key: 'mac_address',
          label: 'MAC',
          sortable: true,
          render: (row) => <span className="list-code">{row.mac_address}</span>,
        },
        {
          key: 'ip_address',
          label: 'IP',
          sortable: true,
          render: (row) => row.ip_address || dash,
        },
        {
          key: 'hostname',
          label: 'Nombre',
          sortable: true,
          render: (row) => row.hostname
            ? <span style={{ fontWeight: 600 }}>{row.hostname}</span>
            : dash,
        },
        {
          key: 'source',
          label: 'Visto en',
          sortable: true,
          render: (row) => <span className="list-badge list-badge--muted">{row.source === 'fdb' ? 'FDB' : 'ARP'}</span>,
        },
        {
          key: 'last_seen_at',
          label: 'Última vez',
          sortable: true,
          sortValue: (row) => row.last_seen_at || '',
          render: (row) => (row.last_seen_at
            ? <span className="list-hint">{hace(row.last_seen_at)}</span>
            : dash),
        },
      ];
    }
    return [
      {
        key: 'name',
        label: 'Nombre',
        sortable: true,
        render: (row) => <span style={{ fontWeight: 600 }}>{row.name || dash}</span>,
      },
      {
        key: 'element_type',
        label: 'Tipo',
        sortable: true,
        render: (row) => (
          <span className="list-badge list-badge--muted">
            {ELEMENT_LABEL[row.element_type] || row.element_type}
          </span>
        ),
      },
      {
        key: 'ip_address',
        label: 'IP de gestión',
        sortable: true,
        render: (row) => row.ip_address || dash,
      },
      {
        key: 'manufacturer',
        label: 'Fabricante / modelo',
        sortable: true,
        sortValue: (row) => `${row.manufacturer || ''} ${row.model || ''}`.trim(),
        render: (row) => (
          <span>
            {row.manufacturer || dash}
            {row.model ? <span className="list-hint"> · {row.model}</span> : null}
          </span>
        ),
      },
      {
        key: 'port_count',
        label: 'Puertos',
        sortable: true,
        render: (row) => (row.port_count ? row.port_count : dash),
      },
      {
        key: 'discovery_source',
        label: 'Origen',
        sortable: true,
        render: (row) => (
          <span className="list-badge">{SOURCE_LABEL[row.discovery_source] || row.discovery_source || '—'}</span>
        ),
      },
      {
        key: 'last_discovery_at',
        label: 'Descubierto',
        sortable: true,
        sortValue: (row) => row.last_discovery_at || '',
        render: (row) => (row.last_discovery_at
          ? <span className="list-hint">{hace(row.last_discovery_at)}</span>
          : dash),
      },
    ];
  }, [esDispositivos, esSsids]);

  if (!companyId) {
    return <div className="alert alert-warning">Selecciona una empresa para ver los descubrimientos.</div>;
  }

  const abrir = (row) => {
    const path = esDispositivos ? ELEMENT_PATH[row.element_type] : null;
    if (path) navigate(`${path}?destacar=${row.id}`);
  };

  return (
    <>
      {error && (
        <div className="list-badge list-badge--off" style={{ display: 'block', marginBottom: 12 }}>
          {error}
        </div>
      )}
      {notice && (
        <div className="list-badge list-badge--on" style={{ display: 'block', marginBottom: 12 }}>
          {notice}
        </div>
      )}

      <ListPage
        icon={SignalIcon}
        title="Dispositivos descubiertos"
        subtitle="Lo que ha encontrado el recolector de red, listo para revisar. Eliminar un equipo descubierto borra su ficha del CMDB; el próximo barrido lo vuelve a crear si sigue respondiendo."
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por nombre, IP, MAC, modelo o serie…"
        filters={[
          {
            key: 'dataset',
            label: 'Conjunto',
            allLabel: 'Dispositivos',
            value: dataset,
            onChange: setDataset,
            options: [
              { value: 'devices', label: 'Dispositivos de red' },
              { value: 'endpoints', label: 'Equipos finales (ARP/FDB)' },
              { value: 'ssids', label: 'SSID / WiFi' },
            ],
          },
        ]}
        exportRows={rows}
        exportFileBase={
          esDispositivos
            ? 'dispositivos-descubiertos'
            : esSsids
              ? 'ssids-descubiertos'
              : 'equipos-finales'
        }
        exportSheetName={esDispositivos ? 'Dispositivos' : esSsids ? 'SSID' : 'Equipos finales'}
        columns={columns}
        columnStorageKey={
          esDispositivos
            ? 'cmdb_cols_net_devices'
            : esSsids
              ? 'cmdb_cols_net_ssids'
              : 'cmdb_cols_net_endpoints'
        }
        rows={rows}
        rowKey={(row) => row.id}
        rowId={(row) => `net-disc-${dataset}-${row.id}`}
        onRowClick={abrir}
        rowTitle={(row) => (
          esDispositivos && ELEMENT_PATH[row.element_type]
            ? 'Abrir la ficha del equipo'
            : ''
        )}
        listKey={esDispositivos ? 'net_devices' : esSsids ? 'net_ssids' : 'net_endpoints'}
        defaultView="list"
        loading={loading}
        loadingText="Cargando descubrimientos…"
        headerActions={(
          <button type="button" className="list-btn-secondary" onClick={cargar}>
            Actualizar
          </button>
        )}
        empty={{
          icon: '🛰️',
          title: esDispositivos
            ? 'Todavía no hay dispositivos descubiertos'
            : esSsids
              ? 'No hay SSID descubiertas'
              : 'No hay equipos finales pendientes',
          subtitle: esDispositivos
            ? 'Configura las credenciales y el ámbito en Agente → Red y lanza un barrido.'
            : esSsids
              ? 'Los SSID salen de la configuración del controlador WiFi y de lo que emiten los APs.'
              : 'Los equipos vistos en ARP/FDB que no son un CI aparecen aquí.',
        }}
        actions={{
          extra: (row) => (esDispositivos ? (
            ELEMENT_PATH[row.element_type] ? (
              <button
                type="button"
                className="list-action-btn"
                title="Abrir la ficha del equipo"
                onClick={(event) => { event.stopPropagation(); abrir(row); }}
              >
                <SignalIcon />
              </button>
            ) : null
          ) : esSsids ? null : (
            <button
              type="button"
              className="list-action-btn"
              title="Ignorar este equipo"
              disabled={pending === row.id}
              onClick={(event) => { event.stopPropagation(); ignorar(row); }}
            >
              <NoSymbolIcon />
            </button>
          )),
          onDelete: esDispositivos
            ? borrarDispositivo
            : esSsids
              ? borrarSsid
              : borrarEndpoint,
          deleteButtonTitle: 'Eliminar este descubierto',
          deleteTitle: esDispositivos
            ? 'Eliminar el equipo descubierto'
            : esSsids
              ? 'Eliminar la SSID descubierta'
              : 'Eliminar el equipo final',
          deleteMessage: (row) => (esDispositivos ? (
            <>
              Se eliminará la ficha <strong>{row.name}</strong> del CMDB, con sus puertos,
              cables y la IP de gestión. <strong>No se puede deshacer.</strong> Si el equipo
              sigue respondiendo, el próximo barrido volverá a darlo de alta.
            </>
          ) : esSsids ? (
            <>
              Se eliminará la SSID descubierta <strong>{row.name}</strong>. Volverá a
              aparecer en el próximo barrido si el controlador la sigue emitiendo.
            </>
          ) : (
            <>
              Se eliminará <strong>{row.mac_address}</strong> de la lista. Si el switch
              lo vuelve a ver en ARP/FDB, reaparecerá en el próximo barrido.
            </>
          )),
        }}
      />
    </>
  );
}
